import { Badge, Box, Card, Group, Paper, rem, SimpleGrid, Stack, Text } from "@mantine/core";
import { CoverTestResult, EyeCoverAction, TestType } from "types/testTypes";
import { CompositeChart } from "@mantine/charts";
import { getEyeCoverActionLabelDetails } from "@utils/UIUtils";
import { computeCoverTestMetrics } from "@utils/test-specific-utils/CoverTestMetricsUtils";
import { useTranslation } from "react-i18next";
import CoverTestEyePathChartAngle from "@components/testContainer/common/CoverTestEyePathChartAngle";
import ObjectivePhoriaTestEntry from "./ObjectivePhoriaTestEntry";

export default function CoverTestEntry({ entry }) {
  const { t } = useTranslation();
  const receivedTestResult = entry as CoverTestResult | null;

  if (!receivedTestResult?.results) {
    return null;
  }
  const result = receivedTestResult.results;

  const {
    overall: overallMetrics,
    leftEye: leftEyeMetrics,
    rightEye: rightEyeMetrics,
    isDataModeInDegrees,
  } = computeCoverTestMetrics(result);

  type RiskLevel = "low" | "moderate" | "high";

  const hasOverallData =
    Number.isFinite(overallMetrics.mean) && Number.isFinite(overallMetrics.stability);

  const riskThresholds = isDataModeInDegrees
    ? { moderateMean: 2.5, highMean: 4.0, moderateStability: 1.8, highStability: 3.0 }
    : { moderateMean: 0.25, highMean: 0.4, moderateStability: 0.18, highStability: 0.3 };

  const riskLevel: RiskLevel = (() => {
    if (!hasOverallData) {
      return "moderate";
    }

    if (
      overallMetrics.mean > riskThresholds.highMean ||
      overallMetrics.stability > riskThresholds.highStability
    ) {
      return "high";
    }
    if (
      overallMetrics.mean > riskThresholds.moderateMean ||
      overallMetrics.stability > riskThresholds.moderateStability
    ) {
      return "moderate";
    }
    return "low";
  })();

  const riskColorMap: Record<RiskLevel, string> = {
    low: "green",
    moderate: "yellow",
    high: "red",
  };

  const riskLabelMap: Record<RiskLevel, string> = {
    low: t("low"),
    moderate: t("moderate"),
    high: t("high"),
  };

  const describeStability = (stability: number) => {
    if (!Number.isFinite(stability)) {
      return t("notEnoughData");
    }
    const stableThreshold = isDataModeInDegrees ? 1.5 : 0.15;
    const variableThreshold = isDataModeInDegrees ? 3.0 : 0.3;
    if (stability <= stableThreshold) {
      return t("stable");
    }
    if (stability <= variableThreshold) {
      return t("variable");
    }
    return t("unstable");
  };

  const formatWithUnit = (value: number) => {
    if (!Number.isFinite(value)) {
      return "—";
    }
    return `${value.toFixed(2)}°`;
  };

  const overallFixationDescriptor = describeStability(overallMetrics.stability);
  const leftFixationDescriptor = describeStability(leftEyeMetrics.stability);
  const rightFixationDescriptor = describeStability(rightEyeMetrics.stability);

  const laypersonDescription = t("extraTests.cover.laypersonExplanation");

  const riskExplanationMap: Record<RiskLevel, string> = {
    low: t("extraTests.cover.lowRiskInterpretation"),
    moderate: t("extraTests.cover.moderateRiskInterpretation"),
    high: t("extraTests.cover.highRiskInterpretation"),
  };

  const objectivePhoria = result.objectivePhoria;
  if (objectivePhoria && receivedTestResult.testType === TestType.ObjectivePhoria) {
    return <ObjectivePhoriaTestEntry objectivePhoria={objectivePhoria} />;
  }

  return (
    <Box>
      <Stack gap="xl" justify="center" align="center" mb={"md"}>
        <Paper withBorder radius="md" p="lg" w="100%">
          <Group justify="space-between" align="flex-start">
            <Stack gap={4}>
              <Text fw={600}>{t("extraTests.cover.alignmentRiskTitle")}</Text>
              <Text size="sm" c="dimmed">
                {t("extraTests.cover.alignmentRiskDescription")}
              </Text>
              <Text size="sm">{laypersonDescription}</Text>
            </Stack>

            <Card p={rem(10)} radius={"lg"}>
              <Group align="center">
                <Badge color={riskColorMap[riskLevel]} size="lg" radius="md">
                  {riskLabelMap[riskLevel]}
                </Badge>
                <Text size="sm">{riskExplanationMap[riskLevel]}</Text>
              </Group>
            </Card>
          </Group>

          <SimpleGrid cols={{ base: 1, md: 3 }} spacing="xl" mt="lg" className="print-cols-3">
            <Stack gap={6}>
              <Text fw={600}>{t("extraTests.cover.overall")}</Text>
              <Text size="sm" c="dimmed">
                {t("extraTests.cover.meanOffset")}
              </Text>
              <Text fw={500}>{formatWithUnit(overallMetrics.mean)}</Text>
              <Text size="sm" c="dimmed">
                {t("extraTests.cover.fixationStability")}
              </Text>
              <Text fw={500}>
                {Number.isFinite(overallMetrics.stability)
                  ? `${formatWithUnit(overallMetrics.stability)} (${overallFixationDescriptor})`
                  : overallFixationDescriptor}
              </Text>
            </Stack>

            <Stack gap={6}>
              <Text fw={600}>{t("leftEye")}</Text>
              <Text size="sm" c="dimmed">
                {t("extraTests.cover.meanOffset")}
              </Text>
              <Text fw={500}>{formatWithUnit(leftEyeMetrics.mean)}</Text>
              <Text size="sm" c="dimmed">
                {t("extraTests.cover.fixationStability")}
              </Text>
              <Text fw={500}>
                {Number.isFinite(leftEyeMetrics.stability)
                  ? `${formatWithUnit(leftEyeMetrics.stability)} (${leftFixationDescriptor})`
                  : leftFixationDescriptor}
              </Text>
            </Stack>

            <Stack gap={6}>
              <Text fw={600}>{t("rightEye")}</Text>
              <Text size="sm" c="dimmed">
                {t("extraTests.cover.meanOffset")}
              </Text>
              <Text fw={500}>{formatWithUnit(rightEyeMetrics.mean)}</Text>
              <Text size="sm" c="dimmed">
                {t("extraTests.cover.fixationStability")}
              </Text>
              <Text fw={500}>
                {Number.isFinite(rightEyeMetrics.stability)
                  ? `${formatWithUnit(rightEyeMetrics.stability)} (${rightFixationDescriptor})`
                  : rightFixationDescriptor}
              </Text>
            </Stack>
          </SimpleGrid>
        </Paper>
        <Paper withBorder radius="md" p="lg" w="100%">
          <SimpleGrid cols={4} spacing="md">
            <CoverTestEyePathChartAngle
              points={result[EyeCoverAction.CoverRightEyeLeftKeptOpen]?.map((snap) =>
                isDataModeInDegrees ? snap.leftGazeAngles : snap.leftGazePosition,
              )}
              eyeCoverAction={EyeCoverAction.CoverRightEyeLeftKeptOpen}
              isGazeAngle={isDataModeInDegrees}
            />
            <CoverTestEyePathChartAngle
              points={result[EyeCoverAction.CoverLeftEyeRightKeptOpen]?.map((snap) =>
                isDataModeInDegrees ? snap.rightGazeAngles : snap.rightGazePosition,
              )}
              eyeCoverAction={EyeCoverAction.CoverLeftEyeRightKeptOpen}
              isGazeAngle={isDataModeInDegrees}
            />
            <CoverTestEyePathChartAngle
              points={result[EyeCoverAction.OpenRightEyeLeftKeptOpen]?.map((snap) =>
                isDataModeInDegrees ? snap.rightGazeAngles : snap.rightGazePosition,
              )}
              eyeCoverAction={EyeCoverAction.OpenLeftEyeRightKeptOpen}
              isGazeAngle={isDataModeInDegrees}
            />
            <CoverTestEyePathChartAngle
              points={result[EyeCoverAction.OpenLeftEyeRightKeptOpen]?.map((snap) =>
                isDataModeInDegrees ? snap.leftGazeAngles : snap.leftGazePosition,
              )}
              eyeCoverAction={EyeCoverAction.OpenRightEyeLeftKeptOpen}
              isGazeAngle={isDataModeInDegrees}
            />
          </SimpleGrid>

          <Box mt="lg" flex={1} w={"100%"} mb={"xl"}>
            <Text size="lg" mb="sm">
              {t("extraTests.cover.distanceFromCenter")}
            </Text>

            <SimpleGrid cols={1} spacing="lg">
              <Box>
                <CompositeChart
                  h={200}
                  mb="lg  "
                  data={result.leftDistanceData}
                  dataKey="timestamp"
                  series={[{ name: "distance", type: "line", color: "blue" }]}
                  composedChartProps={{ syncId: "eyeTimeline" }}
                  xAxisProps={{ hide: true }}
                  yAxisProps={{
                    label: {
                      value: t("extraTests.cover.leftEye"),
                      angle: -90,
                      position: "insideLeft",
                    },
                  }}
                  referenceLines={result.referenceDatas?.map((reference) => ({
                    x: reference.timestamp,
                    color: getEyeCoverActionLabelDetails(reference.eyeCoverAction)?.color,
                    strokeDasharray: "3 3",
                  }))}
                />

                <CompositeChart
                  h={200}
                  data={result.rightDistanceData}
                  dataKey="timestamp"
                  series={[{ name: "distance", type: "line", color: "red" }]}
                  composedChartProps={{ syncId: "eyeTimeline" }}
                  xAxisProps={{
                    label: {
                      value: t("extraTests.cover.timeSeconds"),
                      position: "bottom",
                      offset: 10,
                    },
                  }}
                  yAxisProps={{
                    label: {
                      value: t("extraTests.cover.rightEye"),
                      angle: -90,
                      position: "insideLeft",
                    },
                  }}
                  referenceLines={result.referenceDatas?.map((reference) => ({
                    x: reference.timestamp,
                    label: t(getEyeCoverActionLabelDetails(reference.eyeCoverAction)?.label),
                    color: getEyeCoverActionLabelDetails(reference.eyeCoverAction)?.color,
                    strokeDasharray: "3 3",
                  }))}
                />
              </Box>
            </SimpleGrid>
          </Box>
        </Paper>
      </Stack>
    </Box>
  );
}
